/**
 * The analytics area's view state, in the URL (doc 05-admin/05 §2; doc 01 §5.2).
 *
 * `?date_from=&date_to=` mirror the API one to one and `?city_id=&puja_id=` ride the same
 * namespace, so a pasted link reopens exactly the view the sender saw. A value that is not a
 * real date or a positive id is dropped here, at the boundary, rather than sent to the server
 * to refuse. Empty params mean the screen's own default window.
 */
import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

export interface WindowSearch {
  date_from?: string;
  date_to?: string;
  city_id?: number;
  puja_id?: number;
}

export const WINDOW_KEYS = ['date_from', 'date_to', 'city_id', 'puja_id'] as const;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isoDate(value: unknown): string | undefined {
  if (typeof value !== 'string' || !ISO_DATE.test(value)) return undefined;
  /* `2026-02-30` matches the pattern and is not a day; the round trip catches it. */
  return new Date(`${value}T00:00:00Z`).toISOString().startsWith(value) ? value : undefined;
}

function positiveId(value: unknown): number | undefined {
  const n = typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : undefined;
}

/** Only the keys that hold a valid value — an absent key, never an `undefined` one. */
export function parseWindowSearch(search: Record<string, unknown>): WindowSearch {
  const out: WindowSearch = {};
  const from = isoDate(search.date_from);
  const to = isoDate(search.date_to);
  /* A window is both ends or neither, and never backwards. */
  if (from && to && from <= to) Object.assign(out, { date_from: from, date_to: to });
  const city = positiveId(search.city_id);
  const puja = positiveId(search.puja_id);
  if (city !== undefined) out.city_id = city;
  if (puja !== undefined) out.puja_id = puja;
  return out;
}

/** The search without the analytics keys: what a jump to another module carries. */
export function withoutWindow<T extends object>(search: T): T {
  const rest = { ...search } as Record<string, unknown>;
  for (const k of WINDOW_KEYS) delete rest[k];
  return rest as T;
}

/**
 * What a jump to another module carries: the shell's keys and nothing of the screen it left.
 * A queue's tab and filters (`kyc_status`, `pandits_tab`, doc 05-admin/04 §3.3) and the analytics
 * window belong to the screen they were picked on; carried along they would sit unread in every
 * later URL, and a link copied three screens on would name filters the reader never set.
 *
 * The rule is lexical, so a new screen needs no registration: a screen's keys are
 * `{namespace}_{name}` and the window's are `date_from`/`city_id`-shaped, while the shell's
 * (`theme`, `next`, `token`) never carry an underscore.
 */
export function forModuleJump<T extends object>(search: T): Partial<T> {
  return Object.fromEntries(Object.entries(search).filter(([key]) => !key.includes('_'))) as Partial<T>;
}

/** The ISO window `days` long ending on `end` (inclusive). */
export function windowEnding(days: number, end: Date): { from: string; to: string } {
  const from = new Date(end);
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return { from: from.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}

/** How many days an inclusive ISO window covers. */
export function daysBetween(from: string, to: string): number {
  return (Date.parse(to) - Date.parse(from)) / 86_400_000 + 1;
}

/**
 * The window a screen shows: the URL's when it carries one, else `defaultDays` ending on `end`.
 * `days` names the preset the window matches, or null for a pasted custom range — no preset
 * chip lights up for a window the operator did not pick from the presets.
 */
export function resolveWindow(
  search: WindowSearch,
  presets: readonly number[],
  defaultDays: number,
  end: Date,
): { from: string; to: string; days: number | null } {
  if (search.date_from && search.date_to) {
    const { date_from: from, date_to: to } = search;
    const days = presets.find((d) => {
      const w = windowEnding(d, end);
      return w.from === from && w.to === to;
    });
    return { from, to, days: days ?? null };
  }
  return { ...windowEnding(defaultDays, end), days: defaultDays };
}

/**
 * The URL's window state and a setter. A patch key set to `undefined` removes it. The write
 * replaces the history entry: flicking between presets is not a trail of back-button stops.
 */
export function useWindowSearch(): [WindowSearch, (patch: Partial<Record<keyof WindowSearch, string | number | undefined>>) => void] {
  const search = parseWindowSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const navigate = useNavigate();
  const set = useCallback(
    (patch: Partial<Record<keyof WindowSearch, string | number | undefined>>) =>
      void navigate({
        to: '.',
        replace: true,
        search: (prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev, ...patch };
          for (const k of WINDOW_KEYS) if (next[k] === undefined) delete next[k];
          return next;
        },
      } as never),
    [navigate],
  );
  return [search, set];
}
